import { z } from "zod";
import { activitySchema, customStatusSchema, type Activity, type CustomStatus, type Presence, type PresenceStatusChoice } from "./rich";
import type { CommunityListItem, Media, MessageSearchHit, Post, SearchResults, Topic, UserListItem, UserSummary } from "./types";

/* ───────────── Presence: expiry presets ───────────── */

export const STATUS_EXPIRIES = ["30m", "1h", "4h", "today", "never"] as const;
export type StatusExpiry = (typeof STATUS_EXPIRIES)[number];

/**
 * When a status set now with `preset` should expire. "today" ends at local midnight;
 * pass the client's `Date#getTimezoneOffset()` so the server computes the user's midnight.
 */
export function statusExpiresAt(preset: StatusExpiry, now = new Date(), tzOffsetMinutes?: number): string | null {
  const add = (ms: number) => new Date(now.getTime() + ms).toISOString();
  switch (preset) {
    case "30m":
      return add(30 * 60_000);
    case "1h":
      return add(3600_000);
    case "4h":
      return add(4 * 3600_000);
    case "today": {
      if (tzOffsetMinutes === undefined) {
        const end = new Date(now);
        end.setHours(24, 0, 0, 0);
        return end.toISOString();
      }
      // Shift into the user's local clock, find their next midnight, shift back.
      const local = new Date(now.getTime() - tzOffsetMinutes * 60_000);
      local.setUTCHours(24, 0, 0, 0);
      return new Date(local.getTime() + tzOffsetMinutes * 60_000).toISOString();
    }
    default:
      return null;
  }
}

const expiryInput = {
  /** Preset relative to now; overrides `expiresAt`. */
  expiresIn: z.enum(STATUS_EXPIRIES).optional(),
  /** Minutes behind UTC (Date#getTimezoneOffset), for "today". */
  tzOffset: z.number().int().min(-840).max(840).optional(),
};

export const setPresenceStatusSchema = z.object({ status: z.enum(["online", "idle", "dnd", "invisible"]) });
export const setCustomStatusSchema = customStatusSchema.extend(expiryInput);
export const setActivitySchema = z.union([z.null(), activitySchema.extend(expiryInput), z.object({ activity: z.null() })]);

/** The signed-in user's own presence choices (also on `Me.presence`). */
export interface MyPresence {
  status: PresenceStatusChoice;
  customStatus: CustomStatus | null;
  activity: Activity | null;
}

export interface PresenceBatch {
  items: Presence[];
}

/* ───────────── Global search ───────────── */

export const SEARCH_TYPES = ["all", "users", "communities", "channels", "posts", "messages", "media", "hashtags"] as const;
export type SearchType = (typeof SEARCH_TYPES)[number];
export const SEARCH_HAS = ["image", "video", "file", "link"] as const;
export type SearchHas = (typeof SEARCH_HAS)[number];

const dateish = z
  .string()
  .trim()
  .regex(/^\d{4}-\d{2}-\d{2}(T[\d:.]+(Z|[+-]\d{2}:?\d{2})?)?$/, "Use YYYY-MM-DD");

export const searchQuerySchema = z.object({
  q: z.string().trim().max(200).default(""),
  type: z.enum(SEARCH_TYPES).default("all"),
  /** Username, with or without @. */
  author: z
    .string()
    .trim()
    .max(40)
    .transform((s) => s.replace(/^@/, ""))
    .optional(),
  community: z.string().uuid().optional(),
  channel: z.string().uuid().optional(),
  conversation: z.string().uuid().optional(),
  from: dateish.optional(),
  to: dateish.optional(),
  has: z.enum(SEARCH_HAS).optional(),
  cursor: z.string().max(20).optional(),
  limit: z.coerce.number().int().min(1).max(50).optional(),
});
export type SearchQuery = z.input<typeof searchQuerySchema>;

export interface ChannelSearchHit {
  id: string;
  name: string;
  type: string;
  topic: string;
  community: { id: string; name: string; iconUrl: string | null };
}

export interface MediaSearchHit {
  media: Media;
  source: "post" | "channel" | "dm";
  author: UserSummary;
  createdAt: string;
  postId: string | null;
  messageId: string | null;
  channel: { id: string; name: string } | null;
  community: { id: string; name: string } | null;
  conversationId: string | null;
}

/**
 * GET /api/search. With type=all every section holds a few top hits; with a
 * specific type only that section is filled and `nextCursor` pages through it.
 * `topics` mirrors `hashtags` for older callers.
 */
export interface SearchResponse extends SearchResults {
  users: UserListItem[];
  communities: CommunityListItem[];
  channels: ChannelSearchHit[];
  posts: Post[];
  messages: MessageSearchHit[];
  media: MediaSearchHit[];
  hashtags: Topic[];
  topics: Topic[];
  nextCursor: string | null;
}

/** Where a message hit lives in the app (chat implements jumping to `?m=`). */
export function messageHitPath(h: Pick<MessageSearchHit, "id" | "source" | "channel" | "community" | "conversation">): string {
  if (h.source === "dm" && h.conversation) return `/messages/${h.conversation.id}?m=${h.id}`;
  if (h.community && h.channel) return `/c/${h.community.id}/${h.channel.id}?m=${h.id}`;
  return "/feed";
}

/* ───────────── Mark everything as read ───────────── */

export interface MarkAllReadResult {
  notifications: number;
  channels: number;
  conversations: number;
}
